import { Injectable } from '@angular/core';
import { DroneSurvey } from './drone-survey.service';

/** Minimal shape of the tile coordinate Google passes to getTileUrl. */
export interface TileCoord {
  x: number;
  y: number;
}

/** Inclusive tile range at one zoom level. */
export interface TileExtent {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

/** Web Mercator (EPSG:3857) tile column for a longitude. */
export function lonToTileX(lon: number, zoom: number): number {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, zoom));
}

/** Web Mercator tile row for a latitude. Top-left origin, the XYZ convention. */
export function latToTileY(lat: number, zoom: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * Math.pow(2, zoom)
  );
}

/**
 * Tile range covering the survey at a given zoom, derived from its geographic
 * bounds.
 *
 * Deriving this rather than storing it keeps the API contract to four numbers
 * instead of one range per zoom level. The derived box is occasionally one tile
 * wider than the pyramid actually is, which costs a few requests that return
 * nothing; it is never narrower, so no real tile is ever skipped.
 */
export function tileExtentForZoom(survey: DroneSurvey, zoom: number): TileExtent {
  return {
    xMin: lonToTileX(survey.bounds.west, zoom),
    xMax: lonToTileX(survey.bounds.east, zoom),
    yMin: latToTileY(survey.bounds.north, zoom),
    yMax: latToTileY(survey.bounds.south, zoom)
  };
}

/**
 * Builds the getTileUrl callback for a survey.
 *
 * A free function so it can be tested without the Maps JS API loaded —
 * google.maps.Point is structurally compatible with TileCoord.
 *
 * Returns null outside the survey's zoom range or tile extent, which is how
 * ImageMapType is told there is no tile at that coordinate.
 */
export function createTileUrlResolver(
  survey: DroneSurvey
): (coord: TileCoord, zoom: number) => string | null {
  return (coord: TileCoord, zoom: number): string | null => {
    if (zoom < survey.minZoom || zoom > survey.maxZoom) return null;
    const extent = tileExtentForZoom(survey, zoom);
    if (
      coord.x < extent.xMin || coord.x > extent.xMax ||
      coord.y < extent.yMin || coord.y > extent.yMax
    ) {
      return null;
    }
    // Global replace: a template may legitimately repeat a placeholder, and a
    // single-occurrence replace would leave the rest of them literal.
    return survey.tileUrlTemplate
      .replace(/\{z\}/g, String(zoom))
      .replace(/\{x\}/g, String(coord.x))
      .replace(/\{y\}/g, String(coord.y));
  };
}

/**
 * Renders drone-survey tile pyramids over the base map.
 *
 * Tiles go into the map pane, below the marker and polygon panes, so farm and
 * field boundaries stay visible on top of the imagery.
 */
// Root-provided so the older mapping screen can use it too. A single instance
// is safe because the two screens are separate routes and never mounted at
// once, attach() detaches first, and both callers detach on destroy.
@Injectable({ providedIn: 'root' })
export class MapsTileOverlayService {
  /**
   * Attachments keyed by map, because the legacy field screen shows two maps
   * side by side for comparing analyses and both want the same backdrop.
   * A single attachedTo could only ever serve one of them.
   */
  private attachments = new Map<
    google.maps.Map,
    { overlays: google.maps.ImageMapType[]; previousMaxZoom: number | null | undefined }
  >();

  /**
   * Attach the given surveys, replacing anything already attached.
   *
   * Must be called with a live map instance — callers in components should wrap
   * this in setTimeout so the map has finished initialising (same reason as the
   * fitBounds pattern in maps-landing).
   */
  attach(map: google.maps.Map, surveys: DroneSurvey[], opacity = 1): void {
    // Replace this map's attachment only, leaving any other map alone.
    this.detach(map);
    if (!surveys.length) return;

    // Oldest first, so the newest survey draws on top where they overlap.
    const ordered = [...surveys].sort((a, b) =>
      (a.capturedAt ?? '').localeCompare(b.capturedAt ?? '')
    );

    const overlays: google.maps.ImageMapType[] = [];
    for (const survey of ordered) {
      const overlay = this.buildOverlay(survey, opacity);
      map.overlayMapTypes.push(overlay);
      overlays.push(overlay);
    }

    // Above the deepest survey zoom there are no tiles and the imagery would
    // silently disappear, so cap the map while it is attached.
    const deepest = Math.max(...ordered.map(s => s.maxZoom));
    const previousMaxZoom = map.get('maxZoom');
    map.setOptions({ maxZoom: deepest });

    this.attachments.set(map, { overlays, previousMaxZoom });
  }

  /** Applies to every attached map; the UI exposes one shared control. */
  setOpacity(opacity: number): void {
    this.attachments.forEach(a => a.overlays.forEach(o => o.setOpacity(opacity)));
  }

  /** True when the given map, or any map, currently has imagery attached. */
  isAttached(map?: google.maps.Map): boolean {
    return map ? this.attachments.has(map) : this.attachments.size > 0;
  }

  /** Detach one map, or every map when called with no argument. */
  detach(map?: google.maps.Map): void {
    const targets = map ? [map] : Array.from(this.attachments.keys());
    for (const target of targets) {
      const attachment = this.attachments.get(target);
      if (!attachment) continue;
      const mapOverlays = target.overlayMapTypes;
      for (let i = mapOverlays.getLength() - 1; i >= 0; i--) {
        if (attachment.overlays.includes(mapOverlays.getAt(i) as google.maps.ImageMapType)) {
          mapOverlays.removeAt(i);
        }
      }
      target.setOptions({ maxZoom: attachment.previousMaxZoom ?? null });
      this.attachments.delete(target);
    }
  }

  private buildOverlay(survey: DroneSurvey, opacity: number): google.maps.ImageMapType {
    const resolve = createTileUrlResolver(survey);
    return new google.maps.ImageMapType({
      name: survey.name,
      tileSize: new google.maps.Size(256, 256),
      minZoom: survey.minZoom,
      maxZoom: survey.maxZoom,
      opacity,
      getTileUrl: (coord: google.maps.Point, zoom: number) => resolve(coord, zoom)
    });
  }
}
